import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Badge,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import Navbar from '../../navbar';
import { getConversations, getMessages, sendMessage, markAsRead, getUserById } from '../../../service/messaging';
import { counsellorWebSocket } from '../../../service/websocket';
import CloseIcon from "@mui/icons-material/Close";
const MessagingPage = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Focused');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [remainingTime, setRemainingTime] = useState("");
  const [, setTick] = useState(0);
  const [time, setTime] = useState("");
  const [chipColor, setChipColor] = useState("#22c55e");
  const messagesEndRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const markAsReadTimerRef = useRef(null);

  console.log("messages", messages)

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = user.id;
  const userType = 'doctor'; // Counsellor portal is for doctors

  // Fetch conversations
  const fetchConversations = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const data = await getConversations(userType, userId);

      // Transform API data to component format
      const transformedConversations = (data.conversations || []).map(conv => ({
        id: conv.id,
        name: conv.customer_name || conv.doctor_name || 'Unknown',
        email: conv.other_email,
        app_name: conv.app_name,
        avatar: '/user.png',
        title: conv.other_email,
        lastMessage: conv.last_message || '',
        chat_start_time: conv.chat_start_time || '',
        time: formatTime(conv.last_message_at || conv.created_at),
        unread: conv.unread_count || 0,
        online: false,
        customerId: conv.customer_id,
        doctorId: conv.doctor_id,
        appointmentId: conv.appointment_id,
      }));

      setConversations(transformedConversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   conversations.forEach(conv => {
  //     console.log("conversation customerId:", conv.customerId);
  //   });
  // }, [conversations]);

  useEffect(() => {
    const fetchAllCustomers = async () => {
      if (!conversations || conversations.length === 0) return;

      const fetchedCustomers = await Promise.all(
        conversations.map(async (conv) => {
          try {
            return await getUserById(conv.customerId);
          } catch {
            return null;
          }
        })
      );

      // setCustomers(fetchedCustomers.filter(c => c !== null));
    };

    fetchAllCustomers();
  }, [conversations]);

  // Fetch messages for selected conversation
  const fetchMessages = async (conversationId) => {
    try {
      setMessagesLoading(true);
      const data = await getMessages(conversationId);

      // Transform API data to component format
      const transformedMessages = (data.messages || []).map(msg => ({
        id: msg.id,
        sender: msg.sender_type === userType ? 'me' : 'them',
        text: msg.message_text,
        time: formatMessageTime(msg.created_at),
        date: formatMessageDate(msg.created_at),
        isRead: msg.is_read,
      }));

      // Reverse to show oldest first
      setMessages(transformedMessages.reverse());

      // Mark messages as read
      await markAsRead(conversationId, userType);

      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Handle sending message
  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation) return;

    try {
      const messageData = {
        conversation_id: selectedConversation.id,
        sender_type: userType,
        sender_id: userId,
        sender_name: user.name || '',
        message_text: message.trim(),
        message_type: 'text',
      };

      await sendMessage(messageData);
      setMessage('');

      // Refresh messages
      await fetchMessages(selectedConversation.id);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Format time helper
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+') ? timestamp + 'Z' : timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' });
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp + (timestamp.endsWith('Z') || timestamp.includes('+') ? '' : 'Z'));
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  };

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
  };

  // Setup WebSocket for real-time messaging
  // useEffect(() => {
  //   if (selectedConversation && userId) {
  //     // Connect WebSocket for this conversation
  //     counsellorWebSocket.connect(selectedConversation?.id, userId);

  //     // Handle incoming messages
  //     const messageHandler = (data) => {
  //       if (data.type === 'new_message') {
  //         // New message received
  //         const newMessage = {
  //           id: data.message_id,
  //           sender: data.sender_type === userType ? 'me' : 'them',
  //           text: data.message_text,
  //           time: formatMessageTime(data.created_at),
  //           date: formatMessageDate(data.created_at),
  //           isRead: false,
  //         };

  //         setMessages(prev => [...prev, newMessage]);

  //         // Mark as read if from customer
  //         if (data.sender_type !== userType) {
  //           markAsRead(selectedConversation.id, userType);
  //         }

  //         // Refresh conversations to update unread count
  //         fetchConversations();
  //       } else if (data.type === 'message_history') {
  //         // Initial message history (handled by fetchMessages)
  //       }
  //     };

  //     const connectionHandler = (status) => {
  //       setWsConnected(status === 'connected');
  //       if (status === 'connected') {
  //         console.log('Counsellor WebSocket connected');
  //       } else if (status === 'disconnected') {
  //         console.log('Counsellor WebSocket disconnected - reconnecting...');
  //       }
  //     };

  //     counsellorWebSocket.onMessage(messageHandler);
  //     counsellorWebSocket.onConnectionChange(connectionHandler);

  //     // Cleanup on conversation change or unmount
  //     return () => {
  //       counsellorWebSocket.removeMessageHandler(messageHandler);
  //       counsellorWebSocket.removeConnectionHandler(connectionHandler);
  //       counsellorWebSocket.disconnect();
  //     };
  //   }
  // }, [selectedConversation?.id, userId]);

  // Play a notification chime using Web Audio API (no external file needed)
  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, start, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + duration);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration);
      };
      playTone(830, 0, 0.15);
      playTone(1100, 0.15, 0.2);
    } catch (e) {}
  };

  // Keep ref in sync with selectedConversation state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    if (!userId) return;

    // Connect websocket once for this user
    counsellorWebSocket.connect(userId);

    const messageHandler = (data) => {

      if (data.type === "new_message") {

        // Play notification sound for incoming messages
        if (data.sender_type !== userType) {
          playNotificationSound();
        }

        // Show browser notification for incoming messages
        if (data.sender_type !== userType && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('New Message', {
            body: data.message_text || 'You have a new message',
            icon: '/logo.png',
            tag: 'chat-' + data.conversation_id,
          });
        }

        // ALWAYS update conversation list
        fetchConversations();

        // Only update messages if this conversation is open (use ref for current value)
        const currentConv = selectedConversationRef.current;
        if (currentConv?.id === data.conversation_id) {

          const newMessage = {
            id: data.message_id,
            sender: data.sender_type === userType ? "me" : "them",
            text: data.message_text,
            time: formatMessageTime(data.created_at),
            date: formatMessageDate(data.created_at),
            isRead: false,
          };

          setMessages((prev) => [...prev, newMessage]);

          if (data.sender_type !== userType) {
            // Debounce markAsRead to avoid repeated calls on rapid messages
            if (markAsReadTimerRef.current) {
              clearTimeout(markAsReadTimerRef.current);
            }
            markAsReadTimerRef.current = setTimeout(() => {
              markAsRead(data.conversation_id, userType);
            }, 500);
          }
        }
      }
    };

    const connectionHandler = (status) => {
      setWsConnected(status === "connected");
    };

    counsellorWebSocket.onMessage(messageHandler);
    counsellorWebSocket.onConnectionChange(connectionHandler);

    return () => {
      counsellorWebSocket.removeMessageHandler(messageHandler);
      counsellorWebSocket.removeConnectionHandler(connectionHandler);
      counsellorWebSocket.disconnect();
    };

  }, [userId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [messages]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch conversations on mount
  useEffect(() => {
    if (userId) {
      fetchConversations();
    }
  }, [userId]);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation?.id]);


  const chatStartTime =
    conversations?.find((conv) => conv.id === selectedConversation?.id)
      ?.chat_start_time;

  useEffect(() => {
    if (!chatStartTime) return;

    const interval = setInterval(() => {
      const start = new Date(chatStartTime).getTime();
      const now = new Date().getTime();

      const diff = 15 * 60 * 1000 - (now - start);

      if (diff <= 0) {
        setTime("Chat expired");
        setChipColor("#ef4444");
        clearInterval(interval);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      setTime(`${minutes}:${seconds.toString().padStart(2, "0")}`);

      if (minutes > 5) {
        setChipColor("#22c55e");
      } else if (minutes > 3) {
        setChipColor("#f59e0b");
      } else {
        setChipColor("#ef4444");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [chatStartTime]);

  console.log("selectedConversation", selectedConversation)

  const formatChatDate = (dateString) => {
    const messageDate = new Date(dateString);

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return "Today";
    }

    if (messageDate.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }

    return messageDate.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleCloseChat = () => {
    setSelectedConversation(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f3f2ef' }}>
      <Navbar />

      <Box sx={{ display: 'flex', height: 'calc(100vh - 70px)', pt: 2, px: 3, gap: 2 }}>
        {/* Left Sidebar - Conversations */}
        <Paper
          elevation={0}
          sx={{
            width: 360,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Messaging
              </Typography>
            </Box>

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search messages"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  bgcolor: '#edf3f8',
                  '& fieldset': { border: 'none' },
                },
              }}
            />
          </Box>

          {/* Conversation List */}
          <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : conversations.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, px: 2 }}>
                <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
                  No conversations yet. Start chatting with your patients!
                </Typography>
              </Box>
            ) : (
              conversations.map((conv) => (
                <React.Fragment key={conv.id}>
                  <ListItem
                    button
                    selected={selectedConversation?.id === conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    sx={{
                      py: 2,
                      px: 2,
                      '&:hover': { bgcolor: '#f3f2ef' },
                      '&.Mui-selected': {
                        bgcolor: '#e8f5e9',
                        borderLeft: '3px solid #01754f',
                        '&:hover': { bgcolor: '#e8f5e9' },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        sx={{
                          '& .MuiBadge-badge': {
                            bgcolor: conv.online ? '#22c55e' : 'transparent',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            border: '2px solid white',
                          },
                        }}
                      >
                        <Avatar src={conv.avatar} sx={{ width: 48, height: 48 }} />
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {conv.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {conv.app_name && (
                              <Chip
                                label={conv.app_name}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  bgcolor: '#e0e0e0',       // subtle gray background
                                  color: '#333',             // dark text
                                  fontWeight: 600,
                                  borderRadius: '8px',       // reduced border radius
                                  py: 0.2,                   // vertical padding
                                }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: '#666', fontSize: '0.75rem' }}
                            >
                              {conv.time}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#666',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.85rem',
                              mb: 0.5,
                            }}
                          >
                            {conv.title}
                          </Typography>
                          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: conv.unread > 0 ? '#000' : '#666',
                                fontWeight: conv.unread > 0 ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: conv.unread > 0 ? '200px' : '240px',
                                fontSize: '0.85rem',
                              }}
                            >
                              {conv.lastMessage}
                            </Typography>
                            {conv.unread > 0 && (
                              <Badge
                                badgeContent={conv.unread}
                                color="primary"
                                sx={{
                                  '& .MuiBadge-badge': {
                                    fontSize: '0.7rem',
                                    height: 20,
                                    minWidth: 20,
                                    bgcolor: '#0a66c2',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>

        {/* Right Side - Chat Window */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
          }}
        >
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              {/* <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: selectedConversation.online ? '#22c55e' : '#9ca3af',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        border: '2px solid white',
                      },
                    }}
                  >
                    <Avatar src={selectedConversation.avatar} sx={{ width: 48, height: 48 }} />
                  </Badge>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedConversation.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {selectedConversation.title}
                    </Typography>
                  </Box>
                </Box>
                {selectedConversation && time !== "Chat expired" && (
                  <Chip
                    label={time}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: chipColor,
                      color: "#fff",
                      height: 26,
                      borderRadius: "8px",
                    }}
                  />
                )}
                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
              </Box> */}

              <Box
                sx={{
                  padding: "14px 22px",
                  background: "linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {/* Avatar */}
                {/* <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: selectedConversation?.online ? "#4ADE80" : "#9ca3af",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      border: "2px solid white",
                    },
                  }}
                >
                  <Avatar
                    src={selectedConversation?.avatar}
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.2)",
                    }}
                  />
                </Badge> */}

                {/* Name + Status */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    {selectedConversation?.name}
                  </Typography>

                  <Typography sx={{ fontSize: 11, opacity: 0.85, display: "flex", alignItems: "center" }}>
                    <Box
                      component="span"
                      sx={{
                        width: 7,
                        height: 7,
                        background: selectedConversation?.online ? "#4ADE80" : "#9ca3af",
                        borderRadius: "50%",
                        display: "inline-block",
                        mr: 0.6,
                      }}
                    />
                    {selectedConversation?.title}
                  </Typography>
                </Box>

                {/* Timer Chip */}
                {selectedConversation && time !== "Chat expired" && (
                  <Chip
                    label={time}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: chipColor,
                      color: "#fff",
                      height: 24,
                      borderRadius: "8px",
                      mr: 1,
                    }}
                  />
                )}

                {/* Menu */}
                <IconButton size="small" sx={{ color: "#fff" }} onClick={handleCloseChat}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Messages Area */}
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  overflow: 'auto',
                  bgcolor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {messagesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  // messages.map((msg, index) => (
                  //   <Box key={msg.id}>
                  //     {(index === 0 || msg.date !== messages[index - 1].date) && (
                  //       <Typography
                  //         variant="caption"
                  //         sx={{
                  //           display: 'block',
                  //           textAlign: 'center',
                  //           color: '#666',
                  //           my: 2,
                  //           fontWeight: 600,
                  //         }}
                  //       >
                  //         {msg.date}
                  //       </Typography>
                  //     )}
                  //     <Box
                  //       sx={{
                  //         display: 'flex',
                  //         justifyContent: msg.sender === 'me' ? 'flex-end' : 'flex-start',
                  //         mb: 1,
                  //       }}
                  //     >
                  //       {msg.sender === 'them' && (
                  //         <Avatar src={selectedConversation.avatar} sx={{ width: 32, height: 32, mr: 1 }} />
                  //       )}
                  //       <Box sx={{ maxWidth: '60%' }}>
                  //         {msg.sender === 'them' && (
                  //           <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, ml: 1 }}>
                  //             {selectedConversation.name}
                  //           </Typography>
                  //         )}
                  //         <Paper
                  //           sx={{
                  //             p: 1.5,
                  //             bgcolor: msg.sender === 'me' ? '#0a66c2' : '#f3f2ef',
                  //             color: msg.sender === 'me' ? '#fff' : '#000',
                  //             borderRadius: 2,
                  //             mt: 0.5,
                  //           }}
                  //         >
                  //           <Typography variant="body2">{msg.text}</Typography>
                  //         </Paper>
                  //         <Typography variant="caption" sx={{ color: '#666', ml: 1, mt: 0.5, display: 'block' }}>
                  //           {msg.time}
                  //         </Typography>
                  //       </Box>
                  //     </Box>
                  //   </Box>
                  // ))
                  messages.map((msg, index) => {
                    const showDateDivider =
                      index === 0 || msg.date !== messages[index - 1].date;

                    return (
                      <Box key={msg.id}>

                        {/* Date Divider */}
                        {showDateDivider && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              my: 2,
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: "#E5E7EB",
                                px: 2,
                                py: "3px",
                                borderRadius: "8px",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#444",
                              }}
                            >
                              {formatChatDate(msg.date)}
                            </Box>
                          </Box>
                        )}

                        {/* Message Row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent:
                              msg.sender === "me" ? "flex-end" : "flex-start",
                            mb: 0.8,
                            px: 1,
                          }}
                        >
                          {/* Avatar */}
                          {/* {msg.sender === "them" && (
                            <Avatar
                              src={selectedConversation.avatar}
                              sx={{ width: 30, height: 30, mr: 1 }}
                            />
                          )} */}

                          {/* Bubble */}
                          <Box
                            sx={{
                              maxWidth: "65%",
                              px: "10px",
                              py: "6px",
                              fontSize: 13,
                              lineHeight: 1.4,
                              display: "inline-flex",
                              alignItems: "flex-end",
                              gap: "6px",
                              wordBreak: "break-word",
                              ...(msg.sender === "me"
                                ? {
                                  background:
                                    "linear-gradient(135deg,#22c55e,#14b8a6)",
                                  color: "#fff",
                                  borderRadius: "14px 4px 14px 14px",
                                }
                                : {
                                  background: "#F0FDF4",
                                  color: "#1A2332",
                                  borderRadius: "4px 14px 14px 14px",
                                }),
                            }}
                          >
                            {/* Message */}
                            <span>{msg.text}</span>

                            {/* Time */}
                            <span
                              style={{
                                fontSize: "10px",
                                opacity: 0.75,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {msg.time}
                            </span>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              {/* <Box
                sx={{
                  p: 2,
                  borderTop: '1px solid #e0e0e0',
                  bgcolor: '#fff',
                }}
              > */}
              {/* <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Write a message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton size="small">
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <EmojiIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSendMessage}
                          disabled={!message.trim()}
                          sx={{
                            bgcolor: '#0a66c2',
                            textTransform: 'none',
                            px: 2,
                            '&:hover': { bgcolor: '#004182' },
                          }}
                        >
                          Send
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: '#f3f2ef',
                    },
                  }}
                /> */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  padding: '14px 22px',
                  borderTop: '1px solid rgba(0,0,0,0.06)',
                  background: '#FAFCFB',
                  alignItems: 'center',
                }}
              >
                {/* Attach + Emoji Icons */}
                {/* <Box sx={{ display: 'flex', gap: '4px' }}>
                  <IconButton size="small">
                    <AttachFileIcon sx={{ fontSize: 18 }} />
                  </IconButton>

                  <IconButton size="small">
                    <EmojiIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box> */}

                {/* Message Input */}
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    border: '2px solid rgba(0,0,0,0.08)',
                    borderRadius: '12px',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '13px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#22c55e')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(0,0,0,0.08)')}
                />

                {/* Send Button */}
                <Button
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  sx={{
                    padding: '10px 18px',
                    background: '#22c55e',
                    color: '#fff',
                    borderRadius: '12px',
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'none',
                    minWidth: 'auto',
                    '&:hover': { background: '#16a34a' },
                    '&:disabled': { background: '#ccc', color: '#888' },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </Button>
              </Box>
              {/* </Box> */}
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: '#666' }}>
                Select a conversation to start messaging
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MessagingPage;
